import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * GMC Terrain (linkId 2380).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2380.ts`; shared UI boilerplate
 * (staticTranslations / dealers / saveFormConfig / externalScript) follows
 * 2378. `styleConfig` reuses the GMC palette + `Stratum GMC` face verbatim
 * from 2385/2382/2377.
 *
 * 1.0 gives both Elevation trims the SAME name, disambiguated only by
 * `descriptions` (AWD/FWD) — same pattern as 2378 Traverse's LT-AWD/LT-FWD
 * split. Variant keys here are elevation-awd/elevation-fwd accordingly.
 *
 * Dropped: 1.0's `engine`/`transmission` filterOptions (generic
 * Petrol/Turbo Petrol, CVT/MT/AMT boilerplate found across every GMC project
 * so far, unrelated to any real trim distinction here). `toggleTabs` hides
 * CONFIG (no real filters) — 2384 precedent.
 *
 * Assets live in public/assets/{images,videos}/2380/, resolved by
 * `assetSrc()` in src/utils/metadata.ts.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Terrain: {
      variants: {
        denali: {
          // Year sent to Salesforce in state_info.variant ("YYYY <name>"); API-only.
          leadModelYear: '2026',
          configurationId: 'mlfv94crqnmb',
          name: {
            en: 'Denali',
            ar: 'دينالي',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: '',
          trim: 'Denali',
          highlights: [
            {
              en: '175 HP',
              ar: '175 حصاناً',
            },
            {
              en: '*Torque 275 NM',
              ar: '*عزم الدوران 275 نيوتن متر',
            },
            {
              en: 'Turbo Engine 1.5L',
              ar: 'محرك توربو 1.5 لتر',
            },
          ],
          specs: [
            {
              en: '175 HP',
              ar: '175 حصاناً',
            },
            {
              en: '275 NM',
              ar: '275 نيوتن متر',
            },
          ],
          image: 'images/2380/Denali/DENALI_VARIANT.png',
          price: {
            KSA: 159986,
            UAE: 157000,
            OMR: 16639,
            BHD: 16212,
            QAR: 155293,
            JOD: 30291,
            KW: 13226,
            LBP: 42663,
            IQD: 42663,
            default: 157000,
          },
          basePrice: {
            UAE: 157000,
            KSA: 164500,
            KW: 12500,
            LBP: 57116,
            JOD: 53000,
            BHD: 16141,
            OMR: 16700,
            IQD: 55982246,
            QAR: 157030,
            default: 157000,
          },
          index: 0,
          isDefault: true,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '5SB',
          brandItemCode: 'SCTE03',
        },
        at4: {
          leadModelYear: '2026',
          configurationId: 'mlfv94cirqnmb',
          name: {
            en: 'AT4',
            ar: 'AT4',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: '',
          trim: 'AT4',
          highlights: [
            {
              en: '175 HP',
              ar: '175 حصاناً',
            },
            {
              en: '*Torque 275 NM',
              ar: '*عزم الدوران 275 نيوتن متر',
            },
            {
              en: 'Turbo Engine 1.5L',
              ar: 'محرك توربو 1.5 لتر',
            },
          ],
          specs: [
            {
              en: '175 HP',
              ar: '175 حصاناً',
            },
            {
              en: '275 NM',
              ar: '275 نيوتن متر',
            },
          ],
          image: 'images/2380/at4/AT4_VARIANT.png',
          price: {
            KSA: 144905,
            UAE: 142200,
            OMR: 15070,
            BHD: 14684,
            QAR: 140654,
            JOD: 27435,
            KW: 11979,
            LBP: 38641,
            IQD: 38641,
            default: 142200,
          },
          basePrice: {
            UAE: 142200,
            KSA: 148300,
            KW: 11300,
            OMR: 14700,
            LBP: 51661,
            JOD: 48000,
            BHD: 14603,
            IQD: 50704938,
            QAR: 142227,
            default: 142200,
          },
          index: 1,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '4SB',
          brandItemCode: 'SCTE04',
        },
        'elevation-awd': {
          leadModelYear: '2026',
          configurationId: 'mlfv04cirqnmb',
          name: {
            en: 'Elevation',
            ar: 'Elevation',
          },
          description: {
            en: 'AWD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: 'AWD',
          trim: 'Elevation',
          highlights: [
            {
              en: '175 HP',
              ar: '175 حصاناً',
            },
            {
              en: '*Torque 275 NM',
              ar: '*عزم الدوران 275 نيوتن متر',
            },
            {
              en: 'Turbo Engine 1.5L',
              ar: 'محرك توربو 1.5 لتر',
            },
          ],
          specs: [
            {
              en: '175 HP',
              ar: '175 حصاناً',
            },
            {
              en: '275 NM',
              ar: '275 نيوتن متر',
            },
          ],
          image: 'images/2380/Eleva_AWD/ELIVATION_AWD.png',
          price: {
            KSA: 135326,
            UAE: 132800,
            OMR: 14074,
            BHD: 13713,
            QAR: 131357,
            JOD: 25622,
            KW: 11187,
            LBP: 36087,
            IQD: 36087,
            default: 132800,
          },
          basePrice: {
            UAE: 132800,
            KSA: 137700,
            KW: 10600,
            BHD: 13638,
            JOD: 36900,
            LBP: 39031,
            OMR: 11300,
            IQD: 47353135,
            QAR: 132731,
            default: 132800,
          },
          index: 2,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '3SB',
          brandItemCode: 'SCTE05',
        },
        'elevation-fwd': {
          leadModelYear: '2026',
          configurationId: 'mlfv04cigrqnmb',
          name: {
            en: 'Elevation',
            ar: 'Elevation',
          },
          description: {
            en: 'FWD',
            ar: 'دفع أمامي',
          },
          drivetrain: 'FWD',
          trim: 'Elevation',
          highlights: [
            {
              en: '175 HP',
              ar: '175 حصاناً',
            },
            {
              en: '*Torque 275 NM',
              ar: '*عزم الدوران 275 نيوتن متر',
            },
            {
              en: 'Turbo Engine 1.5L',
              ar: 'محرك توربو 1.5 لتر',
            },
          ],
          specs: [
            {
              en: '175 HP',
              ar: '175 حصاناً',
            },
            {
              en: '275 NM',
              ar: '275 نيوتن متر',
            },
          ],
          image: 'images/2380/Eleva_FWD/ELIVATION_FWD.png',
          price: {
            KSA: 117188,
            UAE: 115000,
            OMR: 12188,
            BHD: 11875,
            QAR: 113750,
            JOD: 22188,
            KW: 9688,
            LBP: 31250,
            IQD: 31250,
            default: 115000,
          },
          basePrice: {
            UAE: 115000,
            KSA: 120000,
            KW: 8600,
            OMR: 11300,
            LBP: 39031,
            BHD: 11810,
            JOD: 36900,
            IQD: 41006104,
            QAR: 114940,
            default: 115000,
          },
          index: 3,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '3SA',
          brandItemCode: 'SCTE05',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2380/CL_2.png',
          title: {
            en: 'City Landscape 1',
            ar: 'منظر المدينة 1',
          },
        },
        g0ZA968_RpUe: {
          image: 'images/2380/CL_1.png',
          title: {
            en: 'City Landscape 2',
            ar: 'منظر المدينة 2',
          },
        },
      },
      ambientLighting: {},
      colors: {
        exterior: {
          g946ignut6zm: {
            label: {
              en: 'Ebony Twilight Metallic',
              ar: 'إيبوني توايلايت معدني',
            },
            image: 'images/2380/Denali/TexC_Ebony Twilight Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GB8',
            brandItemCode: 'EX72',
          },
          '5zwfz8t9v1ci': {
            label: {
              en: 'Glacier White Tricoat',
              ar: 'أبيض جليدي بطبقتين',
            },
            image: 'images/2380/Denali/TexG_Glacier White Tricoat.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX76',
          },
          '3bcwcix9d6ow': {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2380/Denali/TexF_Volcanic Red Tintcoat.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          ekrnov29ywox: {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2380/Denali/TexB_Summit White.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          t97r2gqldscc: {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي معدني',
            },
            image: 'images/2380/Denali/TexE_Sterling_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          e12d2rs52b1b: {
            label: {
              en: 'Downpour Metallic',
              ar: 'مطري معدني',
            },
            image: 'images/2380/Denali/TexH_Downpour_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXP',
            brandItemCode: 'EX21',
          },
          cda85q0n704m: {
            label: {
              en: 'Ebony Twilight Metallic',
              ar: 'إيبوني توايلايت معدني',
            },
            image: 'images/2380/at4/TexC_Ebony_Twilight_Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GB8',
            brandItemCode: 'EX72',
          },
          jn8my849khfa: {
            label: {
              en: 'Rapids Metallic',
              ar: 'رابيدز معدني',
            },
            image: 'images/2380/at4/TexA_Rapids_Metallic.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 7,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAL',
            brandItemCode: 'EX87',
          },
          '8xcbgkact0f0': {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2380/at4/TexF_Volcanic_Red_Tintcoat.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 8,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          '1kghn4i9l1el': {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2380/at4/TexB_Summit_White.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 9,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          ec4xrp8qgmc4: {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي معدني',
            },
            image: 'images/2380/at4/TexE_Sterling_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 10,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          z04z5oe53ocg: {
            label: {
              en: 'Riverstone Metallic',
              ar: 'ريفرستون معدني',
            },
            image: 'images/2380/at4/TexD_Riverstone_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 11,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GFR',
            brandItemCode: 'EX88',
          },
          '07cosz9zjv1c': {
            label: {
              en: 'Ebony Twilight Metallic',
              ar: 'إيبوني توايلايت معدني',
            },
            image: 'images/2380/Eleva_AWD/TexC_Ebony_Twilight_Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 12,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GB8',
            brandItemCode: 'EX72',
          },
          tqyl606wwk4u: {
            label: {
              en: 'Rapids Metallic',
              ar: 'رابيدز معدني',
            },
            image: 'images/2380/Eleva_AWD/TexA_Rapids_Metallic.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 13,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAL',
            brandItemCode: 'EX87',
          },
          kmxu84eillx4: {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2380/Eleva_AWD/TexF_Volcanic_Red_Tintcoat.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 14,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          '0wgwmbez5v85': {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2380/Eleva_AWD/TexB_Summit_White.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 15,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          djljg0jbm4gf: {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي معدني',
            },
            image: 'images/2380/Eleva_AWD/TexE_Sterling_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 16,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          '3ae636l3ri0b': {
            label: {
              en: 'Riverstone Metallic',
              ar: 'ريفرستون معدني',
            },
            image: 'images/2380/Eleva_AWD/TexD_Riverstone_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 17,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GFR',
            brandItemCode: 'EX88',
          },
          '7eez7nplog7a': {
            label: {
              en: 'Ebony Twilight Metallic',
              ar: 'إيبوني توايلايت معدني',
            },
            image: 'images/2380/Eleva_FWD/TexC_Ebony_Twilight_Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 18,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GB8',
            brandItemCode: 'EX72',
          },
          dtjmzz3bsfrl: {
            label: {
              en: 'Rapids Metallic',
              ar: 'رابيدز معدني',
            },
            image: 'images/2380/Eleva_FWD/TexA_Rapids_Metallic.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 19,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAL',
            brandItemCode: 'EX87',
          },
          ruym5z4zk7bp: {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2380/Eleva_FWD/TexF_Volcanic_Red_Tintcoat.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 20,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          svfkt34o1vbs: {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2380/Eleva_FWD/TexB_Summit_White.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 21,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          '2h6rvjratd39': {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي معدني',
            },
            image: 'images/2380/Eleva_FWD/TexE_Sterling_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 22,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          ys6fc5ecm0gr: {
            label: {
              en: 'Riverstone Metallic',
              ar: 'ريفرستون معدني',
            },
            image: 'images/2380/Eleva_FWD/TexD_Riverstone_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 23,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GFR',
            brandItemCode: 'EX88',
          },
        },
        interior: {
          d4xeirch0we2: {
            label: {
              en: 'After Dark',
              ar: 'لون After Dark',
            },
            image: 'images/2380/Denali/After_Dark.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'EKV',
            brandItemCode: 'IN33',
          },
          '5vktfJxuscn42': {
            label: {
              en: 'Light Ash Gray/Very Dark Atmosphere',
              ar: 'رمادي فاتح / بيج داكن جداً',
            },
            image: 'images/2380/Denali/Light_Ash.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'E8N',
            brandItemCode: 'IN58',
          },
          nh3gtor4bx0g: {
            label: {
              en: 'Forest Storm w/ Mahogany Accents',
              ar: 'زيتي داكن مع لمسات الماهوغني',
            },
            image: 'images/2380/at4/Forest_Storm.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 2,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'EKW',
            brandItemCode: 'IN43',
          },
          rjmviyk6rorz: {
            label: {
              en: 'After Dark',
              ar: 'لون After Dark',
            },
            image: 'images/2380/Eleva_AWD/After_Dark.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 3,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'EKV',
            brandItemCode: 'IN33',
          },
          tju83k0v0vm1: {
            label: {
              en: 'Jet Black/ Gideon Gray',
              ar: 'أسود داكن/رمادي جيديون',
            },
            image: 'images/2380/Eleva_AWD/Jet_Black_Gideon.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 4,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'H90',
            brandItemCode: 'IN56',
          },
          '5vktfxuscn42': {
            label: {
              en: 'After Dark',
              ar: 'لون After Dark',
            },
            image: 'images/2380/Eleva_FWD/After_Dark.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 5,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'EKV',
            brandItemCode: 'IN33',
          },
          '5vktfxkuscn42': {
            label: {
              en: 'Jet Black/ Gideon Gray',
              ar: 'أسود داكن/رمادي جيديون',
            },
            image: 'images/2380/Eleva_FWD/Jet_Black.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 6,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'H90',
            brandItemCode: 'IN56',
          },
        },
      },
      features: {
        '3h3fjg5zh489': {
          label: {
            en: 'Chrome Grille',
            ar: 'شبك أمامي من الكروم',
          },
          image: 'images/2380/Denali/Front_Grille.jpg',
          title: {
            en: 'Chrome Grille',
            ar: 'شبك أمامي من الكروم',
          },
          desc: {
            en: 'Chrome Accents & Unique Denali Grille',
            ar: 'لمسات من الكروم وشبك أمامي خاص بطراز دينالي ',
          },
          type: 'exterior',
        },
        ngs1smpm2k89: {
          label: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          image: 'images/2380/Denali/Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          desc: {
            en: '19" Ultra-Bright Machined Aluminum Wheels with Premium Gray Painted Accents',
            ar: 'عجلات ألومنيوم مشغول آلياً فائق السطوع مقاس 19 إنش مع لمسات طلاء رمادية فاخرة',
          },
          type: 'exterior',
        },
        slfnh3hjd6s8: {
          label: {
            en: 'Panaromic Sunroof',
            ar: 'فتحة سقف بانورامية',
          },
          image: 'images/2380/Denali/Panaromic_Sunroof.jpg',
          title: {
            en: 'Panaromic Sunroof',
            ar: 'فتحة سقف بانورامية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        zeq7pry7mouo: {
          label: {
            en: 'Animated Headlamps',
            ar: 'مصابيح أمامية بإضاءة متحركة',
          },
          image: 'images/2380/Denali/Headlamps.jpg',
          title: {
            en: 'Animated Headlamps',
            ar: 'مصابيح أمامية بإضاءة متحركة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        qsf07vxusvhq: {
          label: {
            en: 'Animated Tailamps',
            ar: 'مصابيح خلفية بإضاءة متحركة',
          },
          image: 'images/2380/Denali/Animated_Taillamps.jpg',
          title: {
            en: 'Animated Tailamps',
            ar: 'مصابيح خلفية بإضاءة متحركة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        marg8otdccyk: {
          label: {
            en: 'WOM Lights with Body Color Molding',
            ar: 'مصابيح WOM مع كسوة بلون الهيكل',
          },
          image: 'images/2380/Denali/WOM_Lights.jpg',
          title: {
            en: 'WOM Lights with Body Color Molding',
            ar: 'مصابيح WOM مع كسوة بلون الهيكل',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        oxfitdgg14gk: {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2380/Denali/info.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '15" Diagonal Premium GMC Infotainment System',
            ar: 'نظام المعلومات والترفيه المتميز من GMC مقاس 15 إنش',
          },
          type: 'interior',
        },
        ppj9rls95crn: {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2380/Denali/Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '11” Diagonal Driver Information Center',
            ar: 'مركز معلومات السائق مقاس 11 إنش',
          },
          type: 'interior',
        },
        okumo92efxcj: {
          label: {
            en: 'Head-up Display',
            ar: 'شاشة عرض علوية أمامية',
          },
          image: 'images/2380/Denali/HUD.jpg',
          title: {
            en: 'Head-up Display',
            ar: 'شاشة عرض علوية أمامية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        z5e0bv2uqipu: {
          label: {
            en: 'Seats',
            ar: 'المقاعد',
          },
          image: 'images/2380/Denali/Leather_Seats.jpg',
          title: {
            en: 'Seats',
            ar: 'المقاعد',
          },
          desc: {
            en: 'Perforated leather-appointed seats with heated and ventilated front seats and heated rear outboard seats',
            ar: 'مقاعد جلدية مثقبة مع مقاعد أمامية قابلة للتدفئة والتهوية ومقاعد خلفية جانبية قابلة للتدفئة',
          },
          type: 'interior',
        },
        yfvwasc6mj1o: {
          label: {
            en: 'Front Fascia',
            ar: 'الواجهة الأمامية',
          },
          image: 'images/2380/at4/Front_Fascia.jpg',
          title: {
            en: 'Front Fascia',
            ar: 'الواجهة الأمامية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        pahngkghmoez: {
          label: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          image: 'images/2380/at4/Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          desc: {
            en: '17" Wheels All Terrain Tires',
            ar: 'عجلات مقاس 17 إنش وإطارات لجميع التضاريس',
          },
          type: 'exterior',
        },
        e31d5aitcaee09: {
          label: {
            en: 'Tow Hooks',
            ar: 'حلقات السحب',
          },
          image: 'images/2380/at4/Tow_Hooks.jpg',
          title: {
            en: 'Tow Hooks',
            ar: 'حلقات السحب',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        u236nerccehb: {
          label: {
            en: 'Front Steel Skid Plate',
            ar: 'لوحة للحماية فولاذية أمامية',
          },
          image: 'images/2380/at4/Skid_Plate.jpg',
          title: {
            en: 'Front Steel Skid Plate',
            ar: 'لوحة للحماية فولاذية أمامية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        slfnh3hjd6s0: {
          label: {
            en: 'Panaromic Sunroof',
            ar: 'فتحة سقف بانورامية',
          },
          image: 'images/2380/at4/Panaromic_Sunroof.jpg',
          title: {
            en: 'Panaromic Sunroof',
            ar: 'فتحة سقف بانورامية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        pl0emz85qxbq: {
          label: {
            en: 'Animated Headlamps',
            ar: 'مصابيح أمامية بإضاءة متحركة',
          },
          image: 'images/2380/at4/Headlamps.jpg',
          title: {
            en: 'Animated Headlamps',
            ar: 'مصابيح أمامية بإضاءة متحركة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        e341dagcaee19: {
          label: {
            en: 'Animated Tailamps',
            ar: 'مصابيح خلفية بإضاءة متحركة',
          },
          image: 'images/2380/at4/Animated_Taillamps.jpg',
          title: {
            en: 'Animated Tailamps',
            ar: 'مصابيح خلفية بإضاءة متحركة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        dhj3daio9pk1: {
          label: {
            en: 'WOM Lights with Black Molding',
            ar: 'مصابيح WOM مع كسوة بلون أسود',
          },
          image: 'images/2380/at4/WOM_Lights.jpg',
          title: {
            en: 'WOM Lights with Black Molding',
            ar: 'مصابيح WOM مع كسوة بلون أسود',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        e31d5agotcaee09: {
          label: {
            en: 'Autosense Power Liftgate',
            ar: 'باب خلفي كهربائي يعمل بنظام Autosense',
          },
          image: 'images/2380/at4/Power_Liftgate.jpg',
          title: {
            en: 'Autosense Power Liftgate',
            ar: 'باب خلفي كهربائي يعمل بنظام Autosense',
          },
          desc: {
            en: 'This feature lets you open your liftgate hands-free simply by walking up to the rear of your Terrain with your key fob in your pocket or bag',
            ar: 'تتيح لك هذه الميزة فتح باب صندوق الأمتعة بدون استخدام اليدين بمجرد السير إلى الجزء الخلفي من مركبتك تيرين مع وجود مفتاح التشغيل في جيبك أو حقيبتك',
          },
          type: 'exterior',
        },
        rdkllscjmael: {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2380/at4/Info_Screen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '15" Diagonal Premium GMC Infotainment System',
            ar: 'نظام المعلومات والترفيه المتميز من GMC مقاس 15 إنش',
          },
          type: 'interior',
        },
        fvbmlowhohd5: {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2380/at4/Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '11” Diagonal Driver Information Center',
            ar: 'مركز معلومات السائق مقاس 11 إنش',
          },
          type: 'interior',
        },
        '3ofodapu2ddp': {
          label: {
            en: 'Unique AT4 Interior',
            ar: 'تصميم داخلي فريد من نوعه لسيارة AT4',
          },
          image: 'images/2380/at4/Unique_AT4_Interior.jpg',
          title: {
            en: 'Unique AT4 Interior',
            ar: 'تصميم داخلي فريد من نوعه لسيارة AT4',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        muey3bbku37k: {
          label: {
            en: 'Seats',
            ar: 'المقاعد',
          },
          image: 'images/2380/at4/Premium_Seating.jpg',
          title: {
            en: 'Seats',
            ar: 'المقاعد',
          },
          desc: {
            en: 'Premium driver and front passenger ventilated seats. Core Tec with Cloth Inserts seats with Unique AT4 Interior Accents',
            ar: 'مقاعد أمامية فاخرة للسائق والراكب الأمامي قابلة للتهوية. مقاعد Core Tec مع حشوات من القماش، مع لمسات داخلية فريدة من AT4.',
          },
          type: 'interior',
        },
        muey3bjbku37p: {
          label: {
            en: 'Selectable Drive Mode',
            ar: 'وضع قيادة قابل للاختيار',
          },
          image: 'images/2380/at4/Drive_Mode.jpg',
          title: {
            en: 'Selectable Drive Mode',
            ar: 'وضع قيادة قابل للاختيار',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        b5t3gnisyrtr: {
          label: {
            en: 'Front Fascia',
            ar: 'الواجهة الأمامية',
          },
          image: 'images/2380/Eleva_AWD/Front_Fascia.jpg',
          title: {
            en: 'Front Fascia',
            ar: 'الواجهة الأمامية',
          },
          desc: {
            en: 'A unique upright new fascia, bumpers, grille and LED headlamps and taillamps confidently set your Terrain crossover apart',
            ar: 'واجهة أمامية جديدة فريدة من نوعها، ومصدات، وشبك أمامي، ومصابيح أمامية وخلفية LED تجعل مركبة الكروس أوفر تيرين الخاصة بك متميزة بكل ثقة',
          },
          type: 'exterior',
        },
        '921txph7ctk6': {
          label: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          image: 'images/2380/Eleva_AWD/Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          desc: {
            en: 'Terrain Elevation features 17" Grazen Metallic machined aluminum wheels',
            ar: 'يتميّز تيرين Elevation من الجيل الجديد بعجلات ألومنيوم مشغول آلياً بلون Grazen معدني مقاس 17 إنش',
          },
          type: 'exterior',
        },
        tdkxif15is8l: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED',
          },
          image: 'images/2380/Eleva_AWD/Headlamps.jpg',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED',
          },
          desc: {
            en: 'The Next Generation Terrain Elevation features distinctive LED headlamps complementing its signature GMC styling',
            ar: 'يتميّز تيرين Elevation من الجيل الجديد بمصابيح أمامية LED مميّزة تكمّل تصميم GMC',
          },
          type: 'exterior',
        },
        '2n9ocuhy3xum': {
          label: {
            en: 'LED Taillamps',
            ar: 'مصابيح خلفية LED',
          },
          image: 'images/2380/Eleva_AWD/Taillamps.jpg',
          title: {
            en: 'LED Taillamps',
            ar: 'مصابيح خلفية LED',
          },
          desc: {
            en: 'The Next Generation Terrain Elevation features distinctive LED taillamps, complementing its signature GMC styling',
            ar: 'تتميز مركبة تيرين Elevation من الجيل التالي بمصابيح خلفية LED مميزة، مما يكمل تصميم GMC',
          },
          type: 'exterior',
        },
        nlsuew7en922: {
          label: {
            en: 'Two Tone Roof',
            ar: 'سقف ذو لونين',
          },
          image: 'images/2380/Eleva_AWD/Roof.jpg',
          title: {
            en: 'Two Tone Roof',
            ar: 'سقف ذو لونين',
          },
          desc: {
            en: 'Standout with the available Two Tone roof in Sterling Metallic or Ebony Twilight Metallic',
            ar: 'تتميز المركبة بسقفها ثنائي اللون المتوفر بلونين: فضي معدني أو إيبوني توايلايت معدني',
          },
          type: 'exterior',
        },
        s956dp42ni6i: {
          label: {
            en: '8 Speed Automatic Transmission',
            ar: 'ناقل حركة أوتوماتيكي بـ 8 سرعات',
          },
          image: 'images/2380/Eleva_AWD/Transmission.jpg',
          title: {
            en: '8 Speed Automatic Transmission',
            ar: 'ناقل حركة أوتوماتيكي بـ 8 سرعات',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        '25l2ij6fie1s': {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2380/Eleva_AWD/Info_Screen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '15" Diagonal Premium GMC Infotainment System',
            ar: 'نظام المعلومات والترفيه المتميز من GMC مقاس 15 إنش',
          },
          type: 'interior',
        },
        n1tgoepqbnre: {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2380/Eleva_AWD/Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '11” Diagonal Driver Information Center',
            ar: 'مركز معلومات السائق مقاس 11 إنش',
          },
          type: 'interior',
        },
        av8ukajwzjsm: {
          label: {
            en: 'Front Fascia',
            ar: 'الواجهة الأمامية',
          },
          image: 'images/2380/Eleva_FWD/Front_Fascia.jpg',
          title: {
            en: 'Front Fascia',
            ar: 'الواجهة الأمامية',
          },
          desc: {
            en: 'A unique upright new fascia, bumpers, grille and LED headlamps and taillamps confidently set your Terrain crossover apart',
            ar: 'واجهة أمامية جديدة فريدة من نوعها، ومصدات، وشبك أمامي، ومصابيح أمامية وخلفية LED تجعل مركبة الكروس أوفر تيرين الخاصة بك متميزة بكل ثقة',
          },
          type: 'exterior',
        },
        '56b99wbs39ki': {
          label: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          image: 'images/2380/Eleva_FWD/Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          desc: {
            en: 'Terrain Elevation features 17" Grazen Metallic machined aluminum wheels',
            ar: 'يتميّز تيرين Elevation من الجيل الجديد بعجلات ألومنيوم مشغول آلياً بلون Grazen معدني مقاس 17 إنش',
          },
          type: 'exterior',
        },
        e31dagcaee09: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED',
          },
          image: 'images/2380/Eleva_FWD/Headlamps.jpg',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED',
          },
          desc: {
            en: 'The Next Generation Terrain Elevation features distinctive LED headlamps complementing its signature GMC styling',
            ar: 'يتميّز تيرين Elevation من الجيل الجديد بمصابيح أمامية LED مميّزة تكمّل تصميم GMC',
          },
          type: 'exterior',
        },
        e341dagcaee09: {
          label: {
            en: 'LED Taillamps',
            ar: 'مصابيح خلفية LED',
          },
          image: 'images/2380/Eleva_FWD/Taillamps.jpg',
          title: {
            en: 'LED Taillamps',
            ar: 'مصابيح خلفية LED',
          },
          desc: {
            en: 'The Next Generation Terrain Elevation features distinctive LED taillamps, complementing its signature GMC styling',
            ar: 'تتميز مركبة تيرين Elevation من الجيل التالي بمصابيح خلفية LED مميزة، مما يكمل تصميم GMC',
          },
          type: 'exterior',
        },
        e341raagcaee09: {
          label: {
            en: 'Two Tone Roof',
            ar: 'سقف ذو لونين',
          },
          image: 'images/2380/Eleva_FWD/Roof.jpg',
          title: {
            en: 'Two Tone Roof',
            ar: 'سقف ذو لونين',
          },
          desc: {
            en: 'Standout with the available Two Tone roof in Sterling Metallic or Ebony Twilight Metallic',
            ar: 'تتميز المركبة بسقفها ثنائي اللون المتوفر بلونين: فضي معدني أو إيبوني توايلايت معدني',
          },
          type: 'exterior',
        },
        e31d5agtcaee09: {
          label: {
            en: 'CVT Transmission',
            ar: 'ناقل الحركة CVT',
          },
          image: 'images/2380/Eleva_FWD/Transmission.jpg',
          title: {
            en: 'CVT Transmission',
            ar: 'ناقل الحركة CVT',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        e31dagcaeee09: {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2380/Eleva_FWD/Info_Screen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '15" Diagonal Premium GMC Infotainment System',
            ar: 'نظام المعلومات والترفيه المتميز من GMC مقاس 15 إنش',
          },
          type: 'interior',
        },
        e31dagccaeee09: {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2380/Eleva_FWD/Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '11” Diagonal Driver Information Center',
            ar: 'مركز معلومات السائق مقاس 11 إنش',
          },
          type: 'interior',
        },
      },
      accessories: {
        '8ylilfy10w5s': {
          label: {
            en: 'Variant_Parts_Denali',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        whv4p5vjna3p: {
          label: {
            en: '19In_Midnight Silver_Denali_Default',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        gzf6oq39rp8k: {
          label: {
            en: 'Sunroof_Single Tone',
            ar: '',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        mbxtmb9ivqay: {
          label: {
            en: 'Sterling Metallic roof',
            ar: 'سقف فضي معدني',
          },
          image: 'images/2380/Denali_Roof_Dual_Tone_Sterling_Metallic.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '26R',
          brandItemCode: 'AC221',
        },
        rf9u7yz5tuei: {
          label: {
            en: '20" X 8 " After Midnight Machinefaced Aluminium',
            ar: '20 إنش × 8 إنش من الألومنيوم المغطى آلياً بلون أسود ليلي',
          },
          image: 'images/2380/20_Alloy_Denali.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألومنيوم',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RTU',
          brandItemCode: 'AC132',
        },
        gy73ey0vzxv0: {
          label: {
            en: 'GMC Emblem',
            ar: 'شعار GMC',
          },
          image: 'images/2380/Camera_ACC_GMC_Emblem.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SFZ',
          brandItemCode: 'AC184',
        },
        '0ozjd0gsso4h': {
          label: {
            en: 'Roof Rack Cross Rails Black Pair',
            ar: 'زوج من قضبان السقف المتقاطعة باللون الأسود',
          },
          image: 'images/2380/Roof_Rails_Denali.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VLL',
          brandItemCode: 'AC208',
        },
        vakcid2oshqg: {
          label: {
            en: 'Black Splash Guards',
            ar: 'واقيات الرذاذ السوداء',
          },
          image: 'images/2380/Camera_ACC_Black_Splash_Guards.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VQK',
          brandItemCode: 'AC158',
        },
        y38mjhotgigv: {
          label: {
            en: 'Cargo Net Black Vertical Envelope Style',
            ar: 'شبكة أمتعة سوداء على شكل مظروف عمودي',
          },
          image: 'images/2380/Verical_cargo_Net.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC166',
        },
        jo36p6jnnsmp: {
          label: {
            en: 'Cargo Area Net Black Horizontal',
            ar: 'شبكة منطقة تحميل سوداء أفقية',
          },
          image: 'images/2380/Horizontal_cargo_Net.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC164',
        },
        ofs1ypszw83i: {
          label: {
            en: 'Front Liners Jet Black Front and Rear',
            ar: 'بطانات أمامية باللون الأسود الداكن، في الأمام والخلف',
          },
          image: 'images/2380/Camera_ACC_Front_Liners.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RIA',
          brandItemCode: 'AC183',
        },
        eqlc75r97q78: {
          label: {
            en: 'Cargo Aear Floor Mat Black All-Weather GMC',
            ar: 'سجادة أرضية Cargo Aear سوداء لجميع الأحوال الجوية من GMC',
          },
          image: 'images/2380/Cargo_Mat.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VLI',
          brandItemCode: 'AC163',
        },
        isd2yqtmgupfud: {
          label: {
            en: 'Cargo Organizer Black Collapsible',
            ar: 'منظم الأمتعة الأسود القابل للطي',
          },
          image: 'images/2380/Cargo_Organiser.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC167',
        },
        isd2yqtmgupden: {
          label: {
            en: 'Cargo Organizer Black Collapsible',
            ar: 'منظم الأمتعة الأسود القابل للطي',
          },
          image: 'images/2380/Cargo_Organiser.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC167',
        },
        isd2yqtmgupat4: {
          label: {
            en: 'Cargo Organizer Black Collapsible',
            ar: 'منظم الأمتعة الأسود القابل للطي',
          },
          image: 'images/2380/Cargo_Organiser.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC167',
        },
        isd2yqtmgupawd: {
          label: {
            en: 'Cargo Organizer Black Collapsible',
            ar: 'منظم الأمتعة الأسود القابل للطي',
          },
          image: 'images/2380/Cargo_Organiser.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC167',
        },
        l6yakhg2fh10: {
          label: {
            en: 'Variant_Parts_AT4',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '9kjumt0f4al0': {
          label: {
            en: 'Sunroof_Single Tone',
            ar: '',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        p6z6t4grurfy: {
          label: {
            en: 'Sterling Metallic roof',
            ar: 'سقف فضي معدني',
          },
          image: 'images/2380/AT4_Sunroof_Dual_Tone_Sterling_Metallic.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '26R',
          brandItemCode: 'AC221',
        },
        isd8oqmgpfud: {
          label: {
            en: 'AT4 Emblem',
            ar: 'شعار AT4',
          },
          image: 'images/2380/Camera_ACC_AT4_Emblem.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC145',
        },
        'isd8oqmgpfud-elevation-awd': {
          label: {
            en: 'Terrain Emblem',
            ar: 'شعار تيرين',
          },
          image: 'images/2380/Camera_ACC_Terrain_Emblem.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC224',
        },
        sslppl6edd53: {
          label: {
            en: 'GMC Emblem',
            ar: 'شعار GMC',
          },
          image: 'images/2380/Camera_ACC_GMC_Emblem_AT4.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SFZ',
          brandItemCode: 'AC184',
        },
        x5anixg7je9n: {
          label: {
            en: 'Roof Rack Cross Rails Black Pair',
            ar: 'زوج من قضبان السقف المتقاطعة باللون الأسود',
          },
          image: 'images/2380/Roof_Rails_AT4.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VLL',
          brandItemCode: 'AC208',
        },
        jg6a3grjhx85: {
          label: {
            en: 'Black Splash Guards',
            ar: 'واقيات الرذاذ السوداء',
          },
          image: 'images/2380/Camera_ACC_Black_Splash_Guards.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VQK',
          brandItemCode: 'AC158',
        },
        gvnaujmivz1v: {
          label: {
            en: 'Cargo Net Black Vertical Envelope Style',
            ar: 'شبكة أمتعة سوداء على شكل مظروف عمودي',
          },
          image: 'images/2380/Verical_cargo_Net.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC166',
        },
        ef2994iszfi6: {
          label: {
            en: 'Cargo Area Net Black Horizontal',
            ar: 'شبكة منطقة تحميل سوداء أفقية',
          },
          image: 'images/2380/Horizontal_cargo_Net.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC164',
        },
        dx85isf3vy7c: {
          label: {
            en: 'Front Liners Jet Black Front and Rear',
            ar: 'بطانات أمامية باللون الأسود الداكن، في الأمام والخلف',
          },
          image: 'images/2380/Camera_ACC_Front_Liners.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RIA',
          brandItemCode: 'AC183',
        },
        isd9yqymgupfud: {
          label: {
            en: 'Cargo Aear Floor Mat Black All-Weather GMC',
            ar: 'سجادة أرضية Cargo Aear سوداء لجميع الأحوال الجوية من GMC',
          },
          image: 'images/2380/Cargo_Mat.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VLI',
          brandItemCode: 'AC163',
        },
        '5g1n6syy87ra': {
          label: {
            en: 'Variant_Parts_Elev_AWD',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '4ee31pmiflm1': {
          label: {
            en: 'Roof_Single Tone',
            ar: '',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        erdpdhsmsp3q: {
          label: {
            en: 'Sterling Metallic roof',
            ar: 'سقف فضي معدني',
          },
          image: 'images/2380/Roof_Dual Tone_Sterling_Metallic.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '26R',
          brandItemCode: 'AC221',
        },
        kkbvo88y5tjh: {
          label: {
            en: 'GMC Emblem',
            ar: 'شعار GMC',
          },
          image: 'images/2380/Camera_ACC_GMC_Emblem_Elevation.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SFZ',
          brandItemCode: 'AC184',
        },
        '6rip5ck7x23i': {
          label: {
            en: 'Roof Rack Cross Rails Black Pair',
            ar: 'زوج من قضبان السقف المتقاطعة باللون الأسود',
          },
          image: 'images/2380/Camera_ACC_Roof_Rack_Cross_Rails_Black_Pair.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VLL',
          brandItemCode: 'AC208',
        },
        pxhzgyqjul5i: {
          label: {
            en: 'Black Splash Guards',
            ar: 'واقيات الرذاذ السوداء',
          },
          image: 'images/2380/Camera_ACC_Black_Splash_Guards.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VQK',
          brandItemCode: 'AC158',
        },
        isez790x90nr: {
          label: {
            en: 'Cargo Net Black Vertical Envelope Style',
            ar: 'شبكة أمتعة سوداء على شكل مظروف عمودي',
          },
          image: 'images/2380/Verical_cargo_Net.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC166',
        },
        '8fqmemi1c6s7': {
          label: {
            en: 'Cargo Area Net Black Horizontal',
            ar: 'شبكة منطقة تحميل سوداء أفقية',
          },
          image: 'images/2380/Horizontal_cargo_Net.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC164',
        },
        '25vopaky1sst': {
          label: {
            en: 'Front Liners Jet Black Front and Rear',
            ar: 'بطانات أمامية باللون الأسود الداكن، في الأمام والخلف',
          },
          image: 'images/2380/Camera_ACC_Front_Liners.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RIA',
          brandItemCode: 'AC183',
        },
        g7ttomupjamy: {
          label: {
            en: 'WO_Black_Edition',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        hx52xsy95wz6: {
          label: {
            en: 'W_Black_Edition',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        k1lhqq1uv4kc: {
          label: {
            en: '17Inch Grazen Metallic, Machined Alluminium',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        m32slxx4hjfk: {
          label: {
            en: '19Inch Gloss Black Aluminium painted',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        m32slccxx4hjfk: {
          label: {
            en: 'W_Black_Edition_AWD_Logo',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '3w06jsi5oinw': {
          label: {
            en: 'Variant_Parts_Elev_FWD',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        dpsosp5gxrx3: {
          label: {
            en: 'Roof_Single Tone',
            ar: '',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        prnt5nsa6jxn: {
          label: {
            en: 'Sterling Metallic roof',
            ar: 'سقف فضي معدني',
          },
          image: 'images/2380/Roof_Dual Tone_Sterling_Metallic.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '26R',
          brandItemCode: 'AC221',
        },
        isd8oqmgupfud: {
          label: {
            en: 'GMC Emblem',
            ar: 'شعار GMC',
          },
          image: 'images/2380/Camera_ACC_GMC_Emblem_Elevation.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SFZ',
          brandItemCode: 'AC184',
        },
        isd8oqrmgupfud: {
          label: {
            en: 'Roof Rack Cross Rails Black Pair',
            ar: 'زوج من قضبان السقف المتقاطعة باللون الأسود',
          },
          image: 'images/2380/Camera_ACC_Roof_Rack_Cross_Rails_Black_Pair.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VLL',
          brandItemCode: 'AC208',
        },
        isd8oqymgupfud: {
          label: {
            en: 'Black Splash Guards',
            ar: 'واقيات الرذاذ السوداء',
          },
          image: 'images/2380/Camera_ACC_Black_Splash_Guards.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VQK',
          brandItemCode: 'AC158',
        },
        isd5oqymgupfud: {
          label: {
            en: 'Cargo Net Black Vertical Envelope Style',
            ar: 'شبكة أمتعة سوداء على شكل مظروف عمودي',
          },
          image: 'images/2380/Verical_cargo_Net.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC166',
        },
        isd2oqymgupfud: {
          label: {
            en: 'Cargo Area Net Black Horizontal',
            ar: 'شبكة منطقة تحميل سوداء أفقية',
          },
          image: 'images/2380/Horizontal_cargo_Net.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC164',
        },
        isd9oqymgupfud: {
          label: {
            en: 'Front Liners Jet Black Front and Rear',
            ar: 'بطانات أمامية باللون الأسود الداكن، في الأمام والخلف',
          },
          image: 'images/2380/Camera_ACC_Front_Liners.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RIA',
          brandItemCode: 'AC183',
        },
        isd10yqtmgupfud: {
          label: {
            en: 'WO_Black_Edition',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        isd10yuqtmgupfud: {
          label: {
            en: 'W_Black_Edition',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        isd105yuqtmgupfud: {
          label: {
            en: '17Inch Grazen Metallic, Machined Alluminium',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        isd10y5uqtmgupfud: {
          label: {
            en: '19Inch Gloss Black Aluminium painted',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
      },
      packages: {
        a1rk6oa07y0e: {
          label: {
            en: 'Black Edition Package',
            ar: 'فئة الإصدار الأسود',
          },
          image: 'images/2380/Black_Edition.jpg',
          category: {
            en: 'Packages',
            ar: 'الفئات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'The Black Edition Package comes with 19" high gloss-black aluminum wheels, Darkened grille and C-shaped surrounding bezel, Black roof-mounted side rails, Black exterior accents and badging with red GMC lettering.',
            ar: 'تتضمّن فئة الإصدار الأسود عجلات ألومنيوم سوداء عالية اللمعان مقاس 19 إنش، وشبكاً داكناً مع إطار محيط على شكل حرف C، وقضباناً جانبية سوداء مثبّتة على السقف، ولمسات وشارات خارجية سوداء مع أحرف GMC حمراء.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: '19" high gloss-black aluminum wheels',
                ar: 'عجلات من الألومنيوم الأسود اللامع مقاس 19 إنش',
              },
              accessoryDescription: {
                en: '19" high gloss-black aluminum wheels',
                ar: 'عجلات من الألومنيوم الأسود اللامع مقاس 19 إنش',
              },
              image: 'images/2380/BE_19_Alloy.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Darkened grille and C-shaped surrounding bezel',
                ar: 'شبك داكن مع إطار محيط على شكل حرف C',
              },
              accessoryDescription: {
                en: 'Darkened grille and C-shaped surrounding bezel',
                ar: 'شبك داكن مع إطار محيط على شكل حرف C',
              },
              image: 'images/2380/BE_Grill.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Black roof-mounted side rails',
                ar: 'قضبان جانبية سوداء مثبتة على السقف',
              },
              accessoryDescription: {
                en: 'Black roof-mounted side rails',
                ar: 'قضبان جانبية سوداء مثبتة على السقف',
              },
              image: 'images/2380/BE_Roof_Rails.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Black exterior accents and badging with red GMC lettering',
                ar: 'لمسات وشارات خارجية باللون الأسود وشعار GMC باللون الأحمر',
              },
              accessoryDescription: {
                en: 'Black exterior accents and badging with red GMC lettering',
                ar: 'لمسات وشارات خارجية باللون الأسود وشعار GMC باللون الأحمر',
              },
              image: 'images/2380/BE_Black_Accents.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'WJP',
          brandItemCode: 'PA25',
        },
        x7vtyriufcgr0: {
          label: {
            en: 'Black Edition Package',
            ar: 'فئة الإصدار الأسود',
          },
          image: 'images/2380/Black_Edition.jpg',
          category: {
            en: 'Packages',
            ar: 'الفئات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'The Black Edition Package comes with 19" high gloss-black aluminum wheels, Darkened grille and C-shaped surrounding bezel, Black roof-mounted side rails, Black exterior accents and badging with red GMC lettering.',
            ar: 'تتضمّن فئة الإصدار الأسود عجلات ألومنيوم سوداء عالية اللمعان مقاس 19 إنش، وشبكاً داكناً مع إطار محيط على شكل حرف C، وقضباناً جانبية سوداء مثبّتة على السقف، ولمسات وشارات خارجية سوداء مع أحرف GMC حمراء.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: '19" high gloss-black aluminum wheels',
                ar: 'عجلات من الألومنيوم الأسود اللامع مقاس 19 إنش',
              },
              accessoryDescription: {
                en: '19" high gloss-black aluminum wheels',
                ar: 'عجلات من الألومنيوم الأسود اللامع مقاس 19 إنش',
              },
              image: 'images/2380/BE_19_Alloy.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Darkened grille and C-shaped surrounding bezel',
                ar: 'شبك داكن مع إطار محيط على شكل حرف C',
              },
              accessoryDescription: {
                en: 'Darkened grille and C-shaped surrounding bezel',
                ar: 'شبك داكن مع إطار محيط على شكل حرف C',
              },
              image: 'images/2380/BE_Grill.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Black roof-mounted side rails',
                ar: 'قضبان جانبية سوداء مثبتة على السقف',
              },
              accessoryDescription: {
                en: 'Black roof-mounted side rails',
                ar: 'قضبان جانبية سوداء مثبتة على السقف',
              },
              image: 'images/2380/BE_Roof_Rails.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Black exterior accents and badging with red GMC lettering',
                ar: 'لمسات وشارات خارجية باللون الأسود وشعار GMC باللون الأحمر',
              },
              accessoryDescription: {
                en: 'Black exterior accents and badging with red GMC lettering',
                ar: 'لمسات وشارات خارجية باللون الأسود وشعار GMC باللون الأحمر',
              },
              image: 'images/2380/BE_Black_Accents.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'WJP',
          brandItemCode: 'PA25',
        },
      },
      interactions: {
        a9jk90o1h6fp: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        ygmre5317vts: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        zye1e1vcwmvc: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        q5wycu9jmjsc: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        ehdd0bdxbb0i: {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        yi26t2yj1ipi: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        dhtkbiouot0u: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        '739ajku514pk': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        '7x0dl7w7ygmd': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        o0nbblk83v7e: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        '4pnfkr4uzi0w': {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        jm7u9xug9nel: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        ctalf506071t: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        '8kajaflga6ec': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        pdnnbzpcq1m1: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        '1qkd8e3dqxb2': {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        '9q3d5sn71365': {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        nw7ayiomff7o: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        smjhed8evi5i: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        z50uy8f03jh7: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        '14eisbljp9pi': {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        '1vqrly7ngbhv': {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج',
          },
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقاعد الصف الأول',
          },
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقاعد الصف الثاني',
          },
        },
      },
      name: {
        en: 'GMC Terrain',
        ar: 'GMC تيرين',
      },
      specifications: {
        heading: {
          en: 'GMC Terrain',
          ar: 'GMC تيرين',
        },
        trim: {
          en: 'Denali',
          ar: 'Denali',
        },
        price: {
          en: 'Starting from 157000 MSRP',
          ar: 'ابتداء من 157000 MSRP',
        },
        model: 'GMC Terrain',
        seatingCapacity: '5',
        dimensions: {
          length: '4597 mm',
          width: '',
          height: '',
          wheelbase: '2730 mm',
          groundClearance: '',
        },
        performance: [
          {
            value: '175',
            unit: 'HP',
            label: {
              en: 'Horsepower',
              ar: 'حصان',
            },
          },
          {
            value: '275',
            unit: 'NM',
            label: {
              en: '*Torque',
              ar: '*عزم الدوران',
            },
          },
          {
            value: '1.5L',
            unit: '',
            label: {
              en: 'Turbo Engine',
              ar: 'محرك توربو',
            },
          },
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'الطراز',
            },
            subtitle: {
              en: 'GMC Terrain',
              ar: 'GMC تيرين',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية',
            },
            subtitle: {
              en: 'Glacier White Tricoat (Denali trim only), Summit White, Ebony Twilight Metallic, Volcanic Red Tintcoat, Sterling Metallic, Downpour Metallic (Denali trim only), Rapids Metallic, Riverstone Metallic (Elevation and AT4 trim only)',
              ar: 'أبيض جليدي بثلاث طبقات (في طراز دينالي فقط)، أبيض، إيبوني توايلايت معدني، أحمر بركاني بطبقتين، فضي معدني، مطري معدني (في طراز دينالي فقط)، رابيدز معدني، ريفرستون معدني (متوفر فقط في طرازات Elevation و AT4)',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Interior',
              ar: 'الألوان الداخلية',
            },
            subtitle: {
              en: 'Denali: After Dark, Light Ash Gray/Very Dark Atmosphere / AT4: Forest Storm w/ Mahogany Accents / Elevation: After Dark, Jet Black/ Gideon Gray',
              ar: 'دينالي: لون After Dark، رمادي فاتح / بيج داكن جداً / AT4: زيتي داكن مع لمسات من الماهوغني / Elevation: لون After dark، أسود داكن / رمادي جيديون',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات',
            },
            subtitle: {
              en: '2730 mm',
              ar: '2730 ملم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Length',
              ar: 'الطول',
            },
            subtitle: {
              en: '4597 mm',
              ar: '٤٥٩٧ ملم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Cargo Space',
              ar: 'حيز الأمتعة',
            },
            subtitle: {
              en: 'Behind 1st row seat- 1798.3 litres, Behind 2nd row seat- 843.9 litres',
              ar: 'خلف مقاعد الصف الأول: 1798.3 لتر، خلف مقاعد الصف الثاني: 843.9 لتر',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Transmission',
              ar: 'نقل الحركة',
            },
            subtitle: {
              en: '8-speed Automatic',
              ar: 'ناقل حركة أوتوماتيكي بـ 8 سرعات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar Basics',
              ar: 'باقة اونستار الأساسية',
            },
            subtitle: {
              en: '8 Years',
              ar: '8 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar One',
              ar: 'باقة اونستار ون',
            },
            subtitle: {
              en: 'Upto 3 Years',
              ar: 'حتى 3 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'عدد الركاب',
            },
            subtitle: {
              en: '5',
              ar: '5',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه',
            },
            subtitle: {
              en: '15" Diagonal Premium GMC Infotainment System',
              ar: 'نظام المعلومات والترفيه المتميز من GMC مقاس 15 إنش',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
        ],
      },
      accessoryCategories: {
        exterior: [
          'Roof',
          'Alloy Wheels',
        ],
      },
    },
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: {
        en: 'Select Body Style',
        ar: 'اختر نمط الهيكل',
      },
      availableTrims: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      driveType: {
        en: 'Drive Type',
        ar: 'نوع القيادة',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: {
        en: 'Loading',
        ar: 'جارٍ التحميل',
      },
      variantLoading: {
        en: 'Trim is loading',
        ar: 'نموذج جارٍ التحميل',
      },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      disclaimerNote1: {
        en: '*275 Nm of torque is for all AWD only.',
        ar: '*عزم دوران 275 نيوتن متر متوفر فقط في طراز الدفع الرباعي (AWD).',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: {
        en: 'Go Back',
        ar: 'العودة',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'عرض مميز',
      },
      configurator: {
        en: 'Configurator',
        ar: 'المُكوِّن',
      },
      buildYourRide: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
      saveYourBuild: {
        en: 'Save Your Build',
        ar: 'احفظ تكوينك',
      },
      startingPrice: {
        en: 'Starting Price',
        ar: 'السعر الابتدائي',
      },
      msrp: {
        en: 'MSRP',
        ar: 'سعر التجزئة المقترح',
      },
      proceed: {
        en: 'Proceed',
        ar: 'متابعة',
      },
    },
    tabs: {
      config: {
        en: 'Config',
        ar: 'الإعداد',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلي',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: {
        en: 'Remove',
        ar: 'إزالة',
      },
      packageCategory: {
        en: 'Package',
        ar: 'حزمة',
      },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: {
        en: 'Save to WhatsApp',
        ar: 'حفظ على واتساب',
      },
      optionEmail: {
        en: 'Save to Email',
        ar: 'حفظ على البريد الإلكتروني',
      },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyReadMoreUrl: {
        en: 'https://www.gmcarabia.com/ae-en/privacy',
        ar: 'https://www.gmcarabia.com/ae-ar/privacy',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: {
        en: 'Main Menu',
        ar: 'القائمة الرئيسية',
      },
      search: {
        en: 'Search',
        ar: 'بحث',
      },
      settings: {
        en: 'Settings',
        ar: 'الإعدادات',
      },
      profile: {
        en: 'Profile',
        ar: 'الملف الشخصي',
      },
      notifications: {
        en: 'Notifications',
        ar: 'الإشعارات',
      },
      help: {
        en: 'Help & Support',
        ar: 'المساعدة والدعم',
      },
      filter: {
        en: 'Filter Options',
        ar: 'خيارات التصفية',
      },
      favorites: {
        en: 'Favorites',
        ar: 'المفضلة',
      },
      history: {
        en: 'History',
        ar: 'التاريخ',
      },
      tools: {
        en: 'Tools',
        ar: 'الأدوات',
      },
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'ألوان المقصورة',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      step: {
        en: 'Step',
        ar: 'خطوة',
      },
      openPanel: {
        en: 'Open Panel',
        ar: 'فتح اللوحة',
      },
      closePanel: {
        en: 'Close Panel',
        ar: 'إغلاق اللوحة',
      },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: {
        en: 'Ext',
        ar: 'خارجي',
      },
      int: {
        en: 'Int',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
    },
    bottomDrawer: {
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      interactions: {
        en: 'Interactions',
        ar: 'التفاعلات',
      },
      accessories: {
        en: 'Options & Accessories',
        ar: 'الإكسسوارات',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      highlights: {
        en: 'Highlights',
        ar: 'الميزات البارزة',
      },
      moreOptions: {
        en: 'More Options',
        ar: 'المزيد من الخيارات',
      },
      colorSelector: {
        en: 'Color Selector',
        ar: 'محدد اللون',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      wheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      roofs: {
        en: 'Roofs',
        ar: 'الأسقف',
      },
      performance: {
        en: 'Performance',
        ar: 'الأداء',
      },
      packages: {
        en: 'Packages',
        ar: 'الحزم',
      },
      chargingOptions: {
        en: 'Charging Options',
        ar: 'خيارات الشحن',
      },
      safetyServices: {
        en: 'Safety & Services',
        ar: 'السلامة والخدمات',
      },
      expand: {
        en: 'Expand',
        ar: 'توسيع',
      },
      minimize: {
        en: 'Minimize',
        ar: 'تصغير',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'العرض المميز',
      },
    },
    qr: {
      scan: {
        en: 'Scan QR',
        ar: 'امسح رمز الاستجابة السريعة',
      },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: {
        en: 'Share Configuration',
        ar: 'مشاركة التكوين',
      },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my GMC Terrain configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      whatsappTitle: {
        en: 'Check out my GMC Terrain configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      linkedinTitle: {
        en: 'My GMC Terrain Configuration',
        ar: 'تكويني لسيارة شيفروليه ترافيرس',
      },
      linkedinSummary: {
        en: 'Check out my custom GMC Terrain configuration!',
        ar: 'شاهد تكويني المخصص لسيارة شيفروليه ترافيرس!',
      },
      emailSubject: {
        en: 'Check out my GMC Terrain Configuration',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس',
      },
      emailBody: {
        en: 'I configured my dream GMC Terrain. Take a look:',
        ar: 'قمت بتكوين سيارة شيفروليه ترافيرس المثالية. ألق نظرة:',
      },
      linkLabel: {
        en: 'Or copy link:',
        ar: 'أو انسخ الرابط:',
      },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: {
        en: 'Copy',
        ar: 'نسخ',
      },
      saveAndShare: {
        en: 'Save & Share',
        ar: 'حفظ ومشاركة',
      },
      copyLink: {
        en: 'Copy link',
        ar: 'نسخ الرابط',
      },
      facebook: {
        en: 'Facebook',
        ar: 'Facebook',
      },
      twitter: {
        en: 'Twitter',
        ar: 'Twitter',
      },
      gmail: {
        en: 'Gmail',
        ar: 'Gmail',
      },
      whatsapp: {
        en: 'Whatsapp',
        ar: 'Whatsapp',
      },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: {
        en: 'ex showroom price',
        ar: 'سعر صالة العرض',
      },
      leads: {
        en: 'Save',
        ar: 'حفظ',
      },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      item: {
        en: 'Item',
        ar: 'العنصر',
      },
      items: {
        en: 'Items',
        ar: 'عناصر',
      },
      totalToPay: {
        en: 'Total to pay',
        ar: 'الإجمالي للدفع',
      },
      bookTestDrive: {
        en: 'Save',
        ar: 'حفظ',
      },
      requestQuote: {
        en: 'Request a Quote',
        ar: 'طلب عرض سعر',
      },
      downloadBrochure: {
        en: 'Download Brochure',
        ar: 'تحميل الكتيب',
      },
    },
    priceSummary: {
      title: {
        en: 'Price Summary',
        ar: 'ملخص السعر',
      },
      enterLocation: {
        en: 'Enter Location',
        ar: 'أدخل الموقع',
      },
      selectCountry: {
        en: 'Location',
        ar: 'الموقع',
      },
      selectCity: {
        en: 'Location',
        ar: 'الموقع',
      },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: {
        en: 'Price Breakdown',
        ar: 'تفصيل السعر',
      },
      color: {
        en: 'Color',
        ar: 'اللون',
      },
      variant: {
        en: 'Variant',
        ar: 'الفئة',
      },
      included: {
        en: 'Included',
        ar: 'مشمول',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    featureDialog: {
      playPause: {
        en: 'Play-Pause',
        ar: 'تشغيل-إيقاف',
      },
    },
    selectDealership: {
      title: {
        en: 'Select a Dealer',
        ar: 'اختر وكيلاً',
      },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      dealersInCity: {
        en: 'Dealers in {{city}}',
        ar: 'وكلاء في {{city}}',
      },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      KSA: {
        en: 'KSA',
        ar: 'السعودية',
      },
      KW: {
        en: 'Kuwait',
        ar: 'الكويت',
      },
      default: {
        en: 'UAE',
        ar: 'الإمارات',
      },
    },
    localMatches: {
      exterior: {
        en: 'Exterior',
        ar: 'خارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
      yourConfiguration: {
        en: 'Current Configuration',
        ar: 'التكوين الحالي',
      },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: {
        en: 'Your Build Details',
        ar: 'تفاصيل بنائك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      changeDealer: {
        en: 'Change Dealer',
        ar: 'تغيير الوكيل',
      },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: {
        en: 'Matches found',
        ar: 'تطابق تم العثور عليه',
      },
      startingFrom: {
        en: 'Starting from',
        ar: 'يبدأ من',
      },
      getQuote: {
        en: 'Proceed',
        ar: 'احصل على عرض سعر',
      },
      viewDifferences: {
        en: 'View Differences',
        ar: 'عرض الاختلافات',
      },
      match: {
        en: 'Match',
        ar: 'تطابق',
      },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: {
        en: 'Build Details',
        ar: 'تفاصيل التكوين',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: {
        en: 'Exact Match',
        ar: 'تطابق تام',
      },
      similarMatch: {
        en: 'Close Match',
        ar: 'تطابق قريب',
      },
      availableNow: {
        en: 'Available Now',
        ar: 'متوفر الآن',
      },
      whatYouConfigured: {
        en: 'What you configured',
        ar: 'ما قمت بتكوينه',
      },
      matchingInStock: {
        en: 'Matching vehicle available in stock',
        ar: 'مركبة مطابقة متوفرة في المخزون',
      },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: {
        en: 'Close Match Details',
        ar: 'تفاصيل التطابق القريب',
      },
      matchesYourSelection: {
        en: 'Matches your selection',
        ar: 'يطابق اختيارك',
      },
      sameAsWhatYouSelected: {
        en: 'Same as what you selected',
        ar: 'نفس ما اخترته',
      },
      replacesYourSelection: {
        en: 'Replaces your selection',
        ar: 'يستبدل اختيارك',
      },
      selectionNotIncluded: {
        en: 'Selection not included',
        ar: 'الاختيار غير مشمول',
      },
      includedInThisVehicle: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      additionalFeatureAlreadyIncluded: {
        en: 'Additional feature already included',
        ar: 'ميزة إضافية مشمولة بالفعل',
      },
      stockDisclaimer: {
        en: 'Available stock may include extra features not selected in your original configuration.',
        ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.',
      },
      additionalInstalledOptionsLegend: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      vehicleDetails: {
        en: 'Vehicle Details',
        ar: 'تفاصيل المركبة',
      },
      installedOptionsTitle: {
        en: 'Comes with vehicle',
        ar: 'يأتي مع المركبة',
      },
      additionalInstalledOptionsSubtitle: {
        en: 'Additional Installed Options:',
        ar: 'الخيارات المركبة الإضافية:',
      },
      specTrim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      specExteriorColor: {
        en: 'Exterior Color',
        ar: 'اللون الخارجي',
      },
      specInteriorColor: {
        en: 'Interior Color',
        ar: 'اللون الداخلي',
      },
      specWheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      specPackage: {
        en: 'Package',
        ar: 'الحزمة',
      },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: {
        en: 'Trim Selection',
        ar: 'اختيار الفئة',
      },
    },
    paymentPage: {
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      accessory: {
        en: 'Accessory',
        ar: 'ملحق',
      },
      closePayment: {
        en: 'Close',
        ar: 'إغلاق',
      },
    },
    packageModal: {
      includes: {
        en: 'Includes:',
        ar: 'يشمل:',
      },
      of: {
        en: 'of',
        ar: 'من',
      },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: {
        en: 'Item',
        ar: 'غرض',
      },
      items: {
        en: 'Items',
        ar: 'أغراض',
      },
      addPackage: {
        en: 'Add Package',
        ar: 'إضافة الحزمة',
      },
      noThanks: {
        en: 'No Thanks',
        ar: 'لا شكراً',
      },
      alreadyApplied: {
        en: 'Already Applied',
        ar: 'مُطبّق بالفعل',
      },
    },
    changesRequired: {
      en: 'Changes Required',
      ar: 'التغييرات المطلوبة',
    },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: {
      en: 'Apply Changes',
      ar: 'تطبيق التغييرات',
    },
    previewFeature: {
      en: 'Preview Feature',
      ar: 'معاينة الميزة',
    },
    exploreFeatures: {
      en: 'Explore Features',
      ar: 'استكشف الميزات',
    },
    buildAndPrice: {
      en: 'Build & Price',
      ar: 'البناء والسعر',
    },
    features: {
      preview: {
        en: 'Preview Feature',
        ar: 'معاينة المميزات',
      },
      playAll: {
        en: 'PLAY ALL',
        ar: 'تشغيل الكل',
      },
      title: {
        en: 'Features',
        ar: 'المميزات',
      },
    },
    header: {
      localMatches: {
        en: 'Local\nMatches',
        ar: 'التطابقات\nالمحلية',
      },
      matchingVehicles: {
        en: 'Matching vehicles',
        ar: 'مركبات مطابقة',
      },
      configurationsFound: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
    },
    configPanel: {
      playAll: {
        en: 'Play All',
        ar: 'تشغيل الكل',
      },
      pause: {
        en: 'Pause',
        ar: 'إيقاف مؤقت',
      },
      exteriorColors: {
        en: 'Exterior Colors',
        ar: 'ألوان الهيكل الخارجي',
      },
      interiorColors: {
        en: 'Interior Colors',
        ar: 'ألوان الداخلية',
      },
      roofOptions: {
        en: 'Roof Options',
        ar: 'خيارات السقف',
      },
      interiorOptions: {
        en: 'Interior Options',
        ar: 'خيارات الداخلية',
      },
      exteriorAccessories: {
        en: 'Exterior Accessories',
        ar: 'إكسسوارات خارجية',
      },
      noAdditionalOptions: {
        en: 'No additional options available for the selected trim.',
        ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.',
      },
      noFilters: {
        en: 'No filters available.',
        ar: 'لا توجد فلاتر متاحة.',
      },
      noVariantsMatch: {
        en: 'No trims match the selected filters.',
        ar: 'لا توجد فئات تطابق الفلاتر المختارة.',
      },
      configurationBreakdown: {
        en: 'Configuration Breakdown',
        ar: 'تفاصيل التكوين',
      },
      yourConfiguration: {
        en: 'Your Configuration',
        ar: 'تكوينك',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    conflictDialog: {
      description: {
        en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.',
        ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.',
      },
    },
    viewport: {
      changeBackground: {
        en: 'Change Background',
        ar: 'تغيير الخلفية',
      },
      selectLanguage: {
        en: 'Switch Language (عربي)',
        ar: 'تبديل اللغة (EN)',
      },
      menu: {
        en: 'Menu',
        ar: 'القائمة',
      },
      exploreFeatures: {
        en: 'Explore Features',
        ar: 'استكشف الميزات',
      },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: 'KSA',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: 'KW',
        dealerId: '295892',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Mohammed Bin Al Qassem Street, Al Rai',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني، شارع محمد بن القاسم، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: 'KW',
        dealerId: '122225',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Mohammed Bin Al Qassem Street, Al Rai, Abdul Hameed Salem Sons Co, Al Rai',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني, شارع محمد بن القاسم, الري, شركة أبناء عبد الحميد سالم, الري',
        },
      },
      {
        id: 2,
        country: 'KW',
        value: 'KW',
        dealerId: '260852',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Omar Ibn Al Khatab Street, Sharq',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني، شارع عمر بن الخطاب، شرق',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295876',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Al-Karada Sadoun Street',
          ar: 'شركة ماك الدولية (منطقة حرة)، شارع الكرادة سعدون',
        },
      },
      {
        id: 1,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295879',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Kurdistan District 100 m Street',
          ar: 'ماك إنترناشونال إف زي إي، منطقة كردستان، شارع ١٠٠ متر',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '122234',
        dealerName: 'Mannai Trading Company',
        label: {
          en: 'Mannai Trading Company W.L.L., Salwa E Industrial Street',
          ar: 'شركة مناعي التجارية ذ.م.م، شارع سلوى إي الصناعي',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '295854',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, Al Nahda Main Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق النهضة الرئيسي',
        },
      },
      {
        id: 1,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260877',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, Al Firq Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق الفرق',
        },
      },
      {
        id: 2,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260880',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, New Industrial Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق الصناعية الجديدة',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122227',
        dealerName: 'RYMCO',
        label: {
          en: 'Rasamny Younis Motor Co. SAL, Michael Zakhour Road',
          ar: 'شركة رسامني يونس للسيارات (ش.م.ل)، طريق مايكل زخور',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: false,
    showLocalMatches: true,
    showDisclaimerInPresentation: true,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: true, // per-item prices on cards + summary breakdown, like Corvette
    showDisplayCarPrice: true,
    showCart: false,
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'terrain',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'Terrain',
    modelName: 'Terrain',
    pageTitle: 'Terrain Configurator',
    client: 'GM',
    brand: 'gmc',
    modelId: 'GCTERD2UG',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      provider: 'adobe',
      brand: 'gmc',
      modelName: 'terrain',
      vehicleType: 'suv',
    },
    presentation: {
      video: 'videos/2380/presentation_video_1.webm',
      loadingImage: 'images/2380/sliderImage.webp',
      transitionImage: 'images/2380/transition_logo.png',
      headerLogo: 'images/2380/transition_logo.png',
      headerLogoKeepColor: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: '',
          },
          value: {
            en: '175 HP ',
            ar: '175 حصاناً',
          },
          unit: '',
        },
        {
          index: 1,
          show: true,
          title: {
            en: '*Torque',
            ar: '*عزم الدوران',
          },
          value: {
            en: '275 NM',
            ar: '275 نيوتن متر',
          },
          unit: '',
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'Turbo Engine',
            ar: 'محرك توربو',
          },
          value: {
            en: '1.5L',
            ar: '1.5 لتر',
          },
          unit: '',
        },
      ],
    },
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: [
          'email',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: [
          'whatsapp',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-77110a86d4ec-staging.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
  },
  filterOptions: [],
  styleConfig: {
    cssVariables: {
      '--default-font-family': '\'Stratum GMC\', \'Chevy Sans\', sans-serif',
      '--bold-font-family': '\'Stratum GMC\', \'Chevy Sans Medium\', sans-serif',
      '--brand-primary': '#B9081B',
      '--brand-primary-hover': '#970716',
      '--brand-secondary': '#CC0000',
      '--brand-accent': '#B9081B',
      '--brand-accent-hover': '#970716',
      '--brand-accent-light': '#f7e4e9',
      '--brand-surface': '#f3f3f3',
      '--brand-surface-alt': '#f3f4f6',
      '--brand-text': '#000000',
      '--brand-text-secondary': '#666666',
      '--brand-text-tertiary': '#bebebe',
      '--brand-border': '#e6e6e6',
      '--brand-border-selected': '#B9081B',
      '--brand-card-bg': '#ffffff',
      '--brand-toggle-gradient-from': '#B9081B',
      '--brand-toggle-gradient-to': '#CC0000',
      '--brand-toggle-knob-border': '#B9081B',
      '--brand-accent-secondary': '#CC0000',
      '--brand-card-radius': '2px',
      '--brand-card-radius-sm': '2px',
      '--brand-card-radius-lg': '3px',
      '--brand-button-radius': '2px',
      '--brand-button-radius-sm': '2px',
      '--brand-pill-radius': '50px',
      '--brand-cta-radius': '2px',
      '--brand-button-border': 'var(--brand-accent)',
      '--brand-cta-arrow-bg': '#B9081B',
      '--brand-cta-arrow-fg': '#ffffff',
      '--brand-cta-shimmer': '#B9081B',
      '--brand-cta-loading-bg': '#B9081B',
    },
  },
} as unknown as UIMetadataComplete;
